import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Info, X, HelpCircle } from 'lucide-react';

// Hook personalizado para manejar los modales
export const useModal = () => {
  const [modals, setModals] = useState([]);

  const showAlert = (message, type = 'info', title = '') => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      const modal = {
        id,
        type: 'alert',
        alertType: type,
        title: title || getDefaultTitle(type),
        message,
        onClose: () => {
          setModals(prev => prev.filter(m => m.id !== id));
          resolve(true);
        }
      };
      setModals(prev => [...prev, modal]);
    });
  };

  const showConfirm = (message, title = 'Confirmar') => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      const modal = {
        id,
        type: 'confirm',
        title,
        message,
        onConfirm: () => {
          setModals(prev => prev.filter(m => m.id !== id));
          resolve(true);
        },
        onCancel: () => {
          setModals(prev => prev.filter(m => m.id !== id));
          resolve(false);
        }
      };
      setModals(prev => [...prev, modal]);
    });
  };

  const showPrompt = (message, defaultValue = '', title = 'Ingrese un valor') => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      const modal = {
        id,
        type: 'prompt',
        title,
        message,
        defaultValue,
        onConfirm: (value) => {
          setModals(prev => prev.filter(m => m.id !== id));
          resolve(value);
        },
        onCancel: () => {
          setModals(prev => prev.filter(m => m.id !== id));
          resolve(null);
        }
      };
      setModals(prev => [...prev, modal]);
    });
  };

  return { modals, showAlert, showConfirm, showPrompt };
};

const getDefaultTitle = (type) => {
  switch (type) {
    case 'success': return 'Éxito';
    case 'error': return 'Error';
    case 'warning': return 'Advertencia';
    default: return 'Información';
  }
};

const BrainIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M12 2C8.5 2 6 4.5 6 8c0 1.5.5 2.8 1.3 3.9C6.5 12.5 6 13.7 6 15c0 2.8 2.2 5 5 5h2c2.8 0 5-2.2 5-5 0-1.3-.5-2.5-1.3-3.1C17.5 10.8 18 9.5 18 8c0-3.5-2.5-6-6-6z"
      fill="currentColor"
      opacity="0.3"
    />
    <path
      d="M12 2C8.5 2 6 4.5 6 8c0 1.5.5 2.8 1.3 3.9-.8.6-1.3 1.8-1.3 3.1 0 2.8 2.2 5 5 5h2c2.8 0 5-2.2 5-5 0-1.3-.5-2.5-1.3-3.1C17.5 10.8 18 9.5 18 8c0-3.5-2.5-6-6-6zm0 16h-1c-1.7 0-3-1.3-3-3s1.3-3 3-3h2c1.7 0 3 1.3 3 3s-1.3 3-3 3h-1z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <circle cx="9" cy="7" r="1" fill="currentColor" />
    <circle cx="15" cy="7" r="1" fill="currentColor" />
    <path d="M10 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const getIcon = (type) => {
  const iconProps = { size: 24 };
  switch (type) {
    case 'success': return <CheckCircle className="text-emerald-500" {...iconProps} />;
    case 'error': return <AlertTriangle className="text-red-500" {...iconProps} />;
    case 'warning': return <AlertTriangle className="text-amber-500" {...iconProps} />;
    case 'confirm': return <HelpCircle className="text-blue-500" {...iconProps} />;
    default: return <Info className="text-blue-500" {...iconProps} />;
  }
};

const getColors = (type) => {
  switch (type) {
    case 'success': return 'border-emerald-200 bg-emerald-50';
    case 'error': return 'border-red-200 bg-red-50';
    case 'warning': return 'border-amber-200 bg-amber-50';
    default: return 'border-blue-200 bg-blue-50';
  }
};

// Componente Modal individual con diseño moderno NexusAI
const Modal = ({ modal }) => {
  const [inputValue, setInputValue] = useState(modal.defaultValue || '');
  const [isHovered, setIsHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (modal.type === 'alert') {
          modal.onClose();
        } else if (modal.type === 'confirm') {
          modal.onCancel();
        } else if (modal.type === 'prompt') {
          modal.onCancel();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modal]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      if (modal.type === 'alert') {
        modal.onClose();
      } else if (modal.type === 'confirm') {
        modal.onCancel();
      } else if (modal.type === 'prompt') {
        modal.onCancel();
      }
    }
  };

  // Estilos inline para el modal moderno
  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
    animation: 'fadeIn 0.2s ease-out'
  };

  const modalContainerStyle = {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
    borderRadius: '24px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.3), 0 0 60px rgba(139, 92, 246, 0.15)',
    overflow: 'hidden',
    animation: 'slideUp 0.3s ease-out',
    border: '1px solid rgba(139, 92, 246, 0.2)'
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(79, 70, 229, 0.2) 100%)',
    padding: '24px 28px',
    borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  };

  const iconContainerStyle = {
    width: '52px',
    height: '52px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 16px rgba(139, 92, 246, 0.3)'
  };

  const titleStyle = {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
    flex: 1,
    letterSpacing: '-0.02em'
  };

  const closeButtonStyle = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '12px',
    padding: '10px',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.6)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const contentStyle = {
    padding: '28px',
    background: 'rgba(30, 27, 75, 0.5)'
  };

  const messageStyle = {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '16px',
    lineHeight: '1.7',
    margin: 0,
    whiteSpace: 'pre-line'
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    marginTop: '20px',
    background: 'rgba(255, 255, 255, 0.08)',
    border: '2px solid rgba(139, 92, 246, 0.3)',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
  };

  const footerStyle = {
    padding: '20px 28px',
    background: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid rgba(139, 92, 246, 0.1)'
  };

  const primaryButtonStyle = {
    padding: '14px 28px',
    background: isHovered
      ? 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)'
      : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: isHovered
      ? '0 8px 20px rgba(139, 92, 246, 0.5)'
      : '0 4px 12px rgba(139, 92, 246, 0.3)',
    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
  };

  const secondaryButtonStyle = {
    padding: '14px 28px',
    background: cancelHovered
      ? 'rgba(255, 255, 255, 0.15)'
      : 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  // Inyectar keyframes de animación
  useEffect(() => {
    const styleId = 'nexus-modal-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div style={modalContainerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={iconContainerStyle}>
            <img
              src="/LogoNexus.png"
              alt="NexusAI"
              style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            />
          </div>
          <h3 style={titleStyle}>{modal.title}</h3>
          <button
            style={closeButtonStyle}
            onClick={() => {
              if (modal.type === 'alert') modal.onClose();
              else if (modal.type === 'confirm') modal.onCancel();
              else if (modal.type === 'prompt') modal.onCancel();
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          <p style={messageStyle}>{modal.message}</p>

          {modal.type === 'prompt' && (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={inputStyle}
              placeholder="Ingrese su respuesta..."
              autoFocus
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(139, 92, 246, 0.6)';
                e.target.style.background = 'rgba(255, 255, 255, 0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  modal.onConfirm(inputValue);
                }
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          {modal.type === 'alert' && (
            <button
              style={primaryButtonStyle}
              onClick={modal.onClose}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              Entendido
            </button>
          )}

          {modal.type === 'confirm' && (
            <>
              <button
                style={secondaryButtonStyle}
                onClick={modal.onCancel}
                onMouseEnter={() => setCancelHovered(true)}
                onMouseLeave={() => setCancelHovered(false)}
              >
                Cancelar
              </button>
              <button
                style={primaryButtonStyle}
                onClick={modal.onConfirm}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                Confirmar
              </button>
            </>
          )}

          {modal.type === 'prompt' && (
            <>
              <button
                style={secondaryButtonStyle}
                onClick={modal.onCancel}
                onMouseEnter={() => setCancelHovered(true)}
                onMouseLeave={() => setCancelHovered(false)}
              >
                Cancelar
              </button>
              <button
                style={primaryButtonStyle}
                onClick={() => modal.onConfirm(inputValue)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                Aceptar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente contenedor de modales
export const ModalContainer = ({ modals }) => {
  if (modals.length === 0) return null;

  return (
    <>
      {modals.map((modal) => (
        <Modal key={modal.id} modal={modal} />
      ))}
    </>
  );
};

export default { useModal, ModalContainer };